import React, {useState, useEffect, useRef} from "react";
import styles from "./inputText.module.scss";
import { v4 as uuidv4 } from 'uuid';
import { toast, TOAST_POSITION } from "../material/ToastPopup";

export function DefaultInputText({
  label = "",
  type = "text",
  placeholder = "",
  value,
  onChange = () => {},
  name,
  disabled = false,
  multiline = false,
  rows = 4,
  useUnit = false,
  unit = "",
  autoClearPlaceholder = false,
  onBlur,
  inputStyle = styles.input,
}) {
  const [localPlaceholder, setLocalPlaceholder] = useState(placeholder);

  const handleChange = (e) => {
    onChange?.(e.target.value);
  };

  const allowedTypes = ["text", "number", "email", "password"];
  const inputType = allowedTypes.includes(type) ? type : "text";
  const generateInputName = (label) => (label ? label.replace(/[^\w가-힣]/g, "_") : uuidv4());

  const inputName = name || generateInputName(label);

  const handleFocus = () => {
    if (autoClearPlaceholder) {
      setLocalPlaceholder("");
    }
  };

  const handleBlur = () => {
    if (autoClearPlaceholder) {
      setLocalPlaceholder(placeholder);
    }
  };

  return (
    <div className={`${styles.defaultInput} ${useUnit ? styles.useUnit : ""}`}>
      {label && (
        <label className={styles.label} htmlFor={inputName}>
          {label}
        </label>
      )}
      {multiline ? (
        <textarea
          className={inputStyle}
          id={inputName}
          name={inputName}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          rows={rows}
        />
      ) : (
        <input
          className={inputStyle}
          type={inputType}
          id={inputName}
          name={inputName}
          placeholder={localPlaceholder}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={(e) => {
            handleBlur(e);
            onBlur?.(e);
          }}
          disabled={disabled}
        />
      )}
      {useUnit && <span className={styles.spanUnit}> {unit} </span>}
    </div>
  );
}

export function MinMaxInput({
  type = "min", // "min" 또는 "max"
  counterpartValue = null, // 반대값 (최대일 경우 min값, 최소일 경우 max값)
  value, // 현재 값
  onChange, // 검증된 값만 상위 전달
  unit = "만원", // 단위
}) {
  const [inputValue, setInputValue] = useState(value ?? "");
  const lastValidValueRef = useRef(value); // 이전 유효한 값 저장

  useEffect(() => {
    // value가 변경될 때 유효한 값이면 ref 업데이트
    if (value !== null && value !== undefined && value !== "") {
      const hasCounterpart = counterpartValue !== null && counterpartValue !== undefined && counterpartValue !== "";
      if (hasCounterpart) {
        const parsed = Number(value);
        const counterpartNum = Number(counterpartValue);
        const isValid =
          type === "min"
            ? parsed <= counterpartNum
            : parsed >= counterpartNum;
        
        if (isValid) {
          lastValidValueRef.current = value;
        }
      } else {
        lastValidValueRef.current = value;
      }
    }
    setInputValue(value ?? "");
  }, [value, counterpartValue, type]);

  const handleChange = (val) => {
    // 입력에서 숫자와 소수점만 허용, 소수점 다중 입력 방지
    let sanitized = val.replace(/[^0-9.]/g, "");
    // 소수점이 여러 개 입력되는걸 방지
    const firstDotIdx = sanitized.indexOf(".");
    if (firstDotIdx !== -1) {
      // 첫 소수점 이후의 나머지 .은 모두 삭제
      sanitized =
        sanitized.slice(0, firstDotIdx + 1) + sanitized.slice(firstDotIdx + 1).replace(/\./g, "");
      // 소수점 2자리 초과 시 잘라냄
      const [integerPart, decimalPart] = sanitized.split(".");
      if (decimalPart && decimalPart.length > 2) {
        sanitized = integerPart + "." + decimalPart.slice(0, 2);
      }
    }

    // 쉼표 추가
    let [intPart, decPart] = sanitized.split(".");
    // intPart의 앞자리 불필요 0 제거
    if (intPart) {
      intPart = intPart.replace(/^0+(?=\d)/, "");
    }
    // 3자리마다 콤마
    if (intPart && intPart.length > 0) {
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    let formatted = intPart;
    if (decPart !== undefined) formatted += "." + decPart;

    setInputValue(formatted);
  };

  const handleBlur = () => {
    const blurValue = inputValue.replace(/,/g, "");

    if (blurValue === "") {
      onChange("");
      return;
    }

    const parsed = Number(blurValue);
    const hasCounterpart =
      counterpartValue !== null && counterpartValue !== undefined && counterpartValue !== "";

    if (!isNaN(parsed)) {
      const counterpartNum = Number(counterpartValue);
      const isValid =
        type === "min"
          ? !hasCounterpart || parsed <= counterpartNum
          : !hasCounterpart || parsed >= counterpartNum;

      if (isValid) {
        // 유효한 값이므로 ref 업데이트하고 onChange 호출
        lastValidValueRef.current = parsed;
        onChange(parsed);
      }
      else {
        // 검증 실패: 이전 유효한 값으로 복원
        if(type === "min") {
          toast.warning("최소값은 최대값보다 작아야 합니다.", { position: TOAST_POSITION.BOTTOM_CENTER });
        } else if(type === "max") {
          toast.warning("최대값은 최소값보다 커야 합니다.", { position: TOAST_POSITION.BOTTOM_CENTER });
        }

        const lastValid = lastValidValueRef.current;
        if (lastValid !== null && lastValid !== undefined && lastValid !== "") {
          // 이전 유효한 값으로 입력 필드 복원 (handleChange와 동일한 포맷팅 적용)
          const lastValidStr = lastValid.toString();
          let [intPart, decPart] = lastValidStr.split(".");
          // intPart의 앞자리 불필요 0 제거
          if (intPart) {
            intPart = intPart.replace(/^0+(?=\d)/, "");
          }
          // 3자리마다 콤마
          if (intPart && intPart.length > 0) {
            intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          }
          let formatted = intPart;
          if (decPart !== undefined) formatted += "." + decPart;
          setInputValue(formatted);
        } else {
          // 이전 유효한 값이 없으면 빈 값으로 설정
          setInputValue("");
          onChange("");
        }
      }
    }
  };

  return (
    <DefaultInputText
      type="text"
      placeholder={type === "min" ? "최소" : "최대"}
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      useUnit={true}
      unit={unit}
      autoClearPlaceholder={true}
    />
  );
}
